/**
 * Change password — current + new + confirm with local validation, clean
 * success state. Wired: POST /api/account/change-password. Port of
 * screens-account.jsx ChangePassword.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { InfoNote } from '@/components/gate/bits';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar } from '@/features/community/bits';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { Button, EmptyState, Field, Input, Screen, TopBar } from '@/ui';

export default function ChangePasswordScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [errors, setErrors] = React.useState<{ current?: string; next?: string; confirm?: string }>({});
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);

  const submit = async () => {
    const e: typeof errors = {};
    if (!current) e.current = t('cp_current_error');
    if (next.length < 8 || !/\d/.test(next)) e.next = t('cp_weak');
    if (confirm !== next) e.confirm = t('cp_mismatch');
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/account/change-password', {
        currentPassword: current, newPassword: next,
      });
      setDone(true);
    } catch (err) {
      const msg = (err as { status?: number; error?: string }).status === 401 || (err as { status?: number }).status === 403
        ? t('cp_wrong_current')
        : (err as { error?: string }).error ?? t('cp_generic_error');
      setNotice(msg);
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Screen scroll={false}>
        <TopBar title={t('cp_title')} onBack={goBack} />
        <EmptyState
          icon={<Icon name="check" size={40} color={colors.success500} />}
          title={t('cp_done_title')}
          body={t('cp_done_body')}
          cta={t('am_done')}
          onCta={goBack}
        />
      </Screen>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title={t('cp_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <Field label={t('cp_current')} error={errors.current}>
          <Input value={current} onChangeText={setCurrent} revealable placeholder="••••••••" hasError={!!errors.current} />
        </Field>
        <Field label={t('cp_new')} help={t('cp_weak')} error={errors.next}>
          <Input value={next} onChangeText={setNext} revealable placeholder="••••••••" hasError={!!errors.next} />
        </Field>
        <Field label={t('cp_confirm')} error={errors.confirm}>
          <Input value={confirm} onChangeText={setConfirm} revealable placeholder="••••••••" hasError={!!errors.confirm} />
        </Field>
        {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}
        <View />
      </Screen>
      <BottomBar>
        <Button title={t('cp_update')} size="lg" loading={saving} onPress={() => { void submit(); }} />
      </BottomBar>
    </Screen>
  );
}
